import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import QuickQuote from "@/components/QuickQuote";
import {
  getCountryBySlug,
  getExposByCountry,
  getCitiesByCountry,
} from "@/data/clientsData";
import { breadcrumbSchema, itemListSchema, organizationSchema } from "@/lib/seo-schemas";
import { ExpoCard } from "@/components/ClientGrid";

const CountryPage = () => {
  const { slug } = useParams();
  const country = getCountryBySlug(slug);
  const countryExpos = getExposByCountry(slug);
  const cities = getCitiesByCountry(slug);

  useSEO({
    title: country
      ? `Exhibition Stand Builders in ${country.name} | Stagehook Global`
      : "Country not found",
    description: country
      ? `Stagehook Global is an exhibition stall designer & expo booth builder in ${country.name}. We build hero stands at ${countryExpos
          .map((e) => e.name)
          .join(", ")}. Turnkey design, fabrication & installation.`
      : "",
    keywords: country
      ? `expo booth builder ${country.name}, exhibition stall designer ${country.name}, ${country.name} trade fair stand, ${countryExpos
          .map((e) => `${e.name} stand builder`)
          .join(", ")}`
      : "",
    image: country?.heroImage,
    path: `/country/${slug}`,
    jsonLd: country
      ? [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: country.name, path: `/country/${country.slug}` },
          ]),
          itemListSchema(
            `Expos in ${country.name}`,
            countryExpos.map((e) => ({
              name: `${e.name} — ${e.city} stand builder`,
              path: `/expo/${e.slug}`,
            }))
          ),
        ]
      : null,
  });

  if (!country) return <Navigate to="/404" replace />;

  const totalClients = countryExpos.reduce((s, e) => s + e.clients.length, 0);

  return (
    <main data-testid="country-page" className="pt-[72px]">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden grain">
        <img
          src={country.heroImage}
          alt={`Exhibition stand built by Stagehook Global in ${country.name}`}
          fetchPriority="high"
          decoding="async"
          data-testid="country-hero-img"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,10,0.6) 0%, rgba(7,7,10,0.7) 50%, rgba(7,7,10,0.98) 100%)",
          }}
        />

        <div className="shg-container w-full relative z-10 py-20">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-soft)] hover:text-[var(--red)] mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to all countries
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-5xl">{country.flag}</span>
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--red)]">
              EXPO BOOTH BUILDER · {country.nameLocal.toUpperCase()}
            </div>
          </div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] mb-6 max-w-4xl">
            Exhibition stalls in<br />
            <span style={{ color: "var(--red)" }}>{country.name}</span>.
          </h1>
          <p className="text-lg md:text-xl text-[var(--text-soft)] max-w-2xl">
            {country.intro}
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <span className="flag-chip">
              <Calendar size={12} /> {countryExpos.length} Expos
            </span>
            <span className="flag-chip">
              <MapPin size={12} /> {cities.length} Cities
            </span>
            <span className="flag-chip">
              {totalClients} Stands built
            </span>
          </div>
        </div>
      </section>

      {/* CITIES STRIP */}
      <section data-testid="cities-strip" className="py-10 border-y" style={{ background: "var(--bg-soft)", borderColor: "var(--line)" }}>
        <div className="shg-container flex items-center gap-6 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)]">CITIES WE BUILD IN:</span>
          {cities.map((city) => (
            <span key={city} className="font-display text-xl text-white">
              {city}
              <span className="text-[var(--red)] ml-2">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* EXPOS */}
      <section data-testid="expos-section" className="py-24">
        <div className="shg-container">
          <div className="mb-12 max-w-3xl">
            <div className="section-tag mb-4"><span className="dot" />Expos in {country.name}</div>
            <h2 className="font-display text-5xl leading-[0.95]">
              {countryExpos.length} expos.<br />
              <span style={{ color: "var(--red)" }}>Hundreds of stands.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {countryExpos.map((e) => (
              <ExpoCard key={e.slug} expo={e} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="shg-container">
          <div
            className="rounded-2xl p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6"
            style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}
          >
            <div>
              <div className="shg-eyebrow mb-3">Exhibiting in {country.name}?</div>
              <h3 className="font-display text-3xl md:text-4xl">
                Get your free concept &amp; quote.
              </h3>
            </div>
            <Link to="/contact" className="btn-primary self-start">
              Talk to our team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <section data-testid="country-quote-section" className="pb-20">
        <div className="shg-container max-w-3xl"><QuickQuote expoName={`an expo in ${country.name}`} source="country" /></div>
      </section>
    </main>
  );
};

export default CountryPage;
